import React, { useEffect, useState } from "react";
// import { useNavigate } from 'react-router-dom'
import { api } from "../api/api";
import VehicleCard from "../components/VehicleCard";
import SearchBar from "../components/SearchBar";
import TypeSelect from "../components/TypeSelect";
import DistrictSelect from "../components/DistrictSelect";
import Loader from "../components/Loader";
import { useNavigate } from "react-router-dom";

function VehiclePage({ setLoading, loading }) {
  const [vehicles, setVehicles] = useState([]);
  const [duplicateVehicles, setDuplicateVehicles] = useState([]);
  const [searchKey, setSearchKey] = useState("");
  const [selectedType, setSelectedType] = useState("All Vehicle Types");
  const [selectedDistrict, setSelectedDistrict] = useState("all");
  const [sortByFanRating, setSortByFanRating] = useState(false);
  // Feature 1: date availability filter
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");
  const [unavailableIds, setUnavailableIds] = useState([]);
  // Feature 3: minimum load capacity filter (Lorry & Pickup)
  const [minCapacity, setMinCapacity] = useState("");
  const [loadError, setLoadError] = useState(false);
  // Feature 4: hourly time filter for Lorry/Pickup
  const [fromTime, setFromTime] = useState("");
  const [toTime, setToTime] = useState("");
  const navigate = useNavigate();

  // Pagination states
  const [currentPage, setCurrentPage] = useState(1);
  const [vehiclesPerPage] = useState(20);


  function applyFilters() {
    let filteredVehicles = duplicateVehicles;

    if (selectedType !== "All Vehicle Types") {
      filteredVehicles = filteredVehicles.filter(
        (vehicle) => vehicle.type.toLowerCase() === selectedType.toLowerCase()
      );
    }

    if (selectedDistrict !== "all") {
      filteredVehicles = filteredVehicles.filter(
        (vehicle) =>
          vehicle.district.toLowerCase() === selectedDistrict.toLowerCase()
      );
    }

    if (searchKey.trim()) {
      filteredVehicles = filteredVehicles.filter((vehicle) =>
        vehicle.name.toLowerCase().includes(searchKey.toLowerCase())
      );
    }

    // Feature 1: exclude vehicles booked (pending/accepted) within the selected date range
    if (fromDate && toDate && unavailableIds.length > 0) {
      filteredVehicles = filteredVehicles.filter(
        (vehicle) => !unavailableIds.includes(vehicle._id)
      );
    }

    // Feature 3: filter Lorries/Pickups by minimum load capacity
    if (minCapacity && Number(minCapacity) > 0) {
      filteredVehicles = filteredVehicles.filter((vehicle) => {
        const isLoadVehicle =
          vehicle.type === "Lorry" || vehicle.type === "Pickup";
        return (
          isLoadVehicle &&
          Number(vehicle.maxLoadCapacity) >= Number(minCapacity)
        );
      });
    }

    // Sort by fan rating if enabled
    if (sortByFanRating) {
      filteredVehicles = [...filteredVehicles].sort((a, b) => {
        const avgA = a.fanRatings && a.fanRatings.length > 0
          ? a.fanRatings.reduce((sum, r) => sum + r, 0) / a.fanRatings.length
          : 0;
        const avgB = b.fanRatings && b.fanRatings.length > 0
          ? b.fanRatings.reduce((sum, r) => sum + r, 0) / b.fanRatings.length
          : 0;
        return avgB - avgA; // Descending
      });
    }

    setVehicles(filteredVehicles);
    setCurrentPage(1); // Reset to first page when filters change
  }

  useEffect(() => {
    setLoading(true);
    setLoadError(false);
    api
      .get("/vehicles/getAllVehicles")
      .then((response) => {
        setVehicles(response.data.vehicles || []);
        setDuplicateVehicles(response.data.vehicles || []);
      })
      .catch((error) => {
        console.log(error);
        setLoadError(true);
      })
      .finally(() => setLoading(false));
  }, [setLoading]);

  // Feature 1 + 4: fetch unavailable vehicle IDs for the selected date/time range
  useEffect(() => {
    if (fromDate && toDate) {
      const body = { fromDate, toDate };
      // Pass time window for single-day Lorry/Pickup hourly filtering
      if (fromTime && toTime && fromDate === toDate) {
        body.fromTime = fromTime;
        body.toTime = toTime;
      }
      api
        .post("/bookings/getUnavailableVehicleIds", body)
        .then((response) => setUnavailableIds(response.data.vehicleIds || []))
        .catch((error) => {
          console.log(error);
          setUnavailableIds([]);
        });
    } else {
      setUnavailableIds([]);
    }
  }, [fromDate, toDate, fromTime, toTime]);

  useEffect(() => {
    applyFilters();
  }, [
    selectedType,
    selectedDistrict,
    searchKey,
    sortByFanRating,
    fromDate,
    toDate,
    unavailableIds,
    minCapacity,
  ]);

  // Capacity + time filters only apply to lorries & pick-up trucks
  const isCapacityType = selectedType === "Lorry" || selectedType === "Pickup";

  // Clear capacity, time when switching away from Lorry/Pickup
  useEffect(() => {
    if (selectedType !== "Lorry" && selectedType !== "Pickup") {
      setMinCapacity("");
      setFromTime("");
      setToTime("");
    }
  }, [selectedType]);

  if (loading) {
    return null;
  }

  // Pagination logic
  const indexOfLastVehicle = currentPage * vehiclesPerPage;
  const indexOfFirstVehicle = indexOfLastVehicle - vehiclesPerPage;
  const currentVehicles = vehicles.slice(
    indexOfFirstVehicle,
    indexOfLastVehicle
  );
  const totalPages = Math.ceil(vehicles.length / vehiclesPerPage);

  // Change page
  const paginate = (pageNumber) => setCurrentPage(pageNumber);


  // const navigate = useNavigate();
  return (
    <>
      <div className="flex flex-row justify-center mt-0">
        <div className="flex flex-col max-w-6xl gap-4 mx-5 shadow-lg rounded-lg p-5 bg-white w-full">
          <div className="flex flex-col sm:flex-row gap-2 sm:gap-10 items-center">
            <TypeSelect filterByType={setSelectedType} />
            <DistrictSelect filterByDistrict={setSelectedDistrict} />
            <SearchBar setsearchKey={setSearchKey} />
            <button
              onClick={() => setSortByFanRating(!sortByFanRating)}
              className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-all duration-300 whitespace-nowrap ${sortByFanRating
                  ? 'bg-yellow-400 text-yellow-900 shadow-md'
                  : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                }`}
            >
              <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill={sortByFanRating ? "currentColor" : "none"} viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11.049 2.927c.3-.921 1.603-.921 1.902 0l1.519 4.674a1 1 0 00.95.69h4.915c.969 0 1.371 1.24.588 1.81l-3.976 2.888a1 1 0 00-.363 1.118l1.518 4.674c.3.922-.755 1.688-1.538 1.118l-3.976-2.888a1 1 0 00-1.176 0l-3.976 2.888c-.783.57-1.838-.197-1.538-1.118l1.518-4.674a1 1 0 00-.363-1.118l-3.976-2.888c-.784-.57-.38-1.81.588-1.81h4.914a1 1 0 00.951-.69l1.519-4.674z" />
              </svg>
              {sortByFanRating ? 'Top Rated' : 'Fan Rating'}
            </button>
          </div>

          {/* Feature 1 & 3: availability date range + minimum load capacity */}
          <div className="flex flex-col gap-3 border-t border-gray-100 pt-4">
            {/* Dates always side-by-side (also on mobile) */}
            <div className="grid grid-cols-2 gap-3">
              <div className="flex flex-col">
                <label className="text-xs font-medium text-gray-600 mb-1">Available From</label>
                <input
                  type="date"
                  value={fromDate}
                  max={toDate || undefined}
                  onChange={(e) => setFromDate(e.target.value)}
                  className="w-full border border-gray-300 text-gray-900 text-sm rounded-lg focus:ring-[#6F48E5] focus:border-[#6F48E5] focus:outline-none p-2.5"
                />
              </div>
              <div className="flex flex-col">
                <label className="text-xs font-medium text-gray-600 mb-1">Available To</label>
                <input
                  type="date"
                  value={toDate}
                  min={fromDate || undefined}
                  onChange={(e) => setToDate(e.target.value)}
                  className="w-full border border-gray-300 text-gray-900 text-sm rounded-lg focus:ring-[#6F48E5] focus:border-[#6F48E5] focus:outline-none p-2.5"
                />
              </div>
            </div>

            {/* Capacity only for Lorry/Pickup, under the dates, full width */}
            {isCapacityType && (
              <div className="flex flex-col">
                <label className="text-xs font-medium text-gray-600 mb-1">Min. Capacity (kg)</label>
                <input
                  type="text"
                  inputMode="numeric"
                  value={minCapacity}
                  onChange={(e) =>
                    setMinCapacity(
                      e.target.value.replace(/[^\d]/g, "").replace(/^0+/, "")
                    )
                  }
                  placeholder="e.g. 1000"
                  className="w-full border border-gray-300 text-gray-900 text-sm rounded-lg focus:ring-[#6F48E5] focus:border-[#6F48E5] focus:outline-none p-2.5"
                />
              </div>
            )}

            {/* Feature 4: hourly time filter — only for Lorry/Pickup on a single day */}
            {isCapacityType && fromDate && fromDate === toDate && (
              <div className="grid grid-cols-2 gap-3">
                <div className="flex flex-col">
                  <label className="text-xs font-medium text-gray-600 mb-1">From Time</label>
                  <select
                    value={fromTime}
                    onChange={(e) => { setFromTime(e.target.value); setToTime(""); }}
                    className="w-full border border-gray-300 text-gray-900 text-sm rounded-lg focus:ring-[#6F48E5] focus:border-[#6F48E5] focus:outline-none p-2.5 bg-white"
                  >
                    <option value="">Any time</option>
                    {Array.from({ length: 16 }, (_, i) => i + 6).map((h) => {
                      const val = `${String(h).padStart(2, "0")}:00`;
                      const lbl = h < 12 ? `${h}:00 AM` : h === 12 ? "12:00 PM" : `${h - 12}:00 PM`;
                      return <option key={val} value={val}>{lbl}</option>;
                    })}
                  </select>
                </div>
                <div className="flex flex-col">
                  <label className="text-xs font-medium text-gray-600 mb-1">To Time</label>
                  <select
                    value={toTime}
                    onChange={(e) => setToTime(e.target.value)}
                    disabled={!fromTime}
                    className="w-full border border-gray-300 text-gray-900 text-sm rounded-lg focus:ring-[#6F48E5] focus:border-[#6F48E5] focus:outline-none p-2.5 bg-white disabled:opacity-50"
                  >
                    <option value="">Any time</option>
                    {Array.from({ length: 17 }, (_, i) => i + 6)
                      .filter((h) => fromTime && `${String(h).padStart(2, "0")}:00` > fromTime)
                      .map((h) => {
                        const val = `${String(h).padStart(2, "0")}:00`;
                        const lbl = h < 12 ? `${h}:00 AM` : h === 12 ? "12:00 PM" : `${h - 12}:00 PM`;
                        return <option key={val} value={val}>{lbl}</option>;
                      })}
                  </select>
                </div>
              </div>
            )}

            {(fromDate || toDate || minCapacity || fromTime || toTime) && (
              <button
                onClick={() => {
                  setFromDate("");
                  setToDate("");
                  setMinCapacity("");
                  setFromTime("");
                  setToTime("");
                }}
                className="self-start px-4 py-2 rounded-lg text-sm font-medium bg-gray-100 text-gray-600 hover:bg-gray-200 transition-all duration-300"
              >
                Clear
              </button>
            )}
          </div>
        </div>
      </div>


      <div className="flex flex-col items-center">
        {loadError ? (
          <div className="max-w-xl w-full mx-5 mt-16 text-center bg-white rounded-lg shadow p-8">
            <p className="text-lg font-semibold text-gray-800 mb-2">Couldn't load vehicles</p>
            <p className="text-gray-500 mb-4">
              We had trouble reaching the server. Please check your connection and try again.
            </p>
            <button
              onClick={() => window.location.reload()}
              className="px-5 py-2 rounded-lg bg-[#6F48E5] text-white font-medium hover:bg-[#5a3ac4] transition-colors"
            >
              Retry
            </button>
          </div>
        ) : currentVehicles.length === 0 ? (
          <div className="max-w-xl w-full mx-5 mt-16 text-center bg-white rounded-lg shadow p-8">
            <p className="text-lg font-semibold text-gray-800 mb-2">No vehicles found</p>
            <p className="text-gray-500">
              Try adjusting your filters — vehicle type, location, dates, or minimum capacity.
            </p>
          </div>
        ) : (
          <div className="max-w-6xl grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 xl:grid-cols-4 gap-10 md:gap-2 lg:gap-5 mx-10 sm:mx-10 mt-10">
            {currentVehicles.map((vehicle, index) => (
              <div key={index}>
                <VehicleCard vehicle={vehicle}></VehicleCard>
              </div>
            ))}
          </div>
        )}

        {/* Simplified Pagination Component */}
        {vehicles.length > vehiclesPerPage && (
          <div className="flex justify-center mt-10 mb-10">
            <nav aria-label="Pagination">
              <ul className="flex items-center">
                {/* Previous Button */}
                <li>
                  <button
                    onClick={() => currentPage > 1 && paginate(currentPage - 1)}
                    disabled={currentPage === 1}
                    className={`px-3 py-1 mx-1 rounded-md ${currentPage === 1
                        ? "bg-gray-200 text-gray-500 cursor-not-allowed"
                        : "bg-white text-gray-700 hover:bg-gray-100 border border-gray-300"
                      }`}
                  >
                    Previous
                  </button>
                </li>

                {/* First Page */}
                {totalPages > 3 && (
                  <li>
                    <button
                      onClick={() => paginate(1)}
                      className={`px-3 py-1 mx-1 rounded-md ${currentPage === 1
                          ? "bg-[#6f48e5] text-white"
                          : "bg-white text-gray-700 hover:bg-gray-100 border border-gray-300"
                        }`}
                    >
                      1
                    </button>
                  </li>
                )}

                {/* Ellipsis if needed */}
                {currentPage > 2 && totalPages > 3 && (
                  <li>
                    <span className="px-2 py-1 mx-1">...</span>
                  </li>
                )}

                {/* Current Page (if not first or last) */}
                {currentPage !== 1 && currentPage !== totalPages && (
                  <li>
                    <button className="px-3 py-1 mx-1 rounded-md bg-[#6f48e5] text-white">
                      {currentPage}
                    </button>
                  </li>
                )}

                {/* Ellipsis if needed */}
                {currentPage < totalPages - 1 && totalPages > 3 && (
                  <li>
                    <span className="px-2 py-1 mx-1">...</span>
                  </li>
                )}

                {/* Last Page */}
                {totalPages > 1 && (
                  <li>
                    <button
                      onClick={() => paginate(totalPages)}
                      className={`px-3 py-1 mx-1 rounded-md ${currentPage === totalPages
                          ? "bg-[#6f48e5] text-white"
                          : "bg-white text-gray-700 hover:bg-gray-100 border border-gray-300"
                        }`}
                    >
                      {totalPages}
                    </button>
                  </li>
                )}

                {/* Next Button */}
                <li>
                  <button
                    onClick={() =>
                      currentPage < totalPages && paginate(currentPage + 1)
                    }
                    disabled={currentPage === totalPages}
                    className={`px-3 py-1 mx-1 rounded-md ${currentPage === totalPages
                        ? "bg-gray-200 text-gray-500 cursor-not-allowed"
                        : "bg-white text-gray-700 hover:bg-gray-100 border border-gray-300"
                      }`}
                  >
                    Next
                  </button>
                </li>
              </ul>
            </nav>
          </div>
        )}

        {/* Display page info */}
        {!loadError && vehicles.length > 0 && (
          <div className="text-gray-600 mb-10">
            Showing {indexOfFirstVehicle + 1}-
            {Math.min(indexOfLastVehicle, vehicles.length)} of {vehicles.length}{" "}
            vehicles
          </div>
        )}
      </div>
    </>
  );
}

export default VehiclePage;
